import type { CSSProperties, ReactNode } from 'react';

import type { PortalConfig } from '@/shared/api/resources';
import { LogoHorizontal, LogoSymbol } from '@/shared/ui/Logo';

const HEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Endereco de imagem que pode virar CSS.
 *
 * O servidor ja recusa outro esquema, e a tela confere de novo antes de escrever o
 * valor no estilo: um valor arbitrario num url() de CSS e um vetor de injecao.
 */
function imagemSegura(endereco: string): string | null {
  const limpo = endereco.trim();
  if (!limpo) return null;
  if (
    /^https?:\/\//i.test(limpo) ||
    (limpo.startsWith('/') && !limpo.startsWith('//'))
  ) {
    return limpo;
  }
  return null;
}

/** Fundo do painel de marca: imagem, degrade da empresa ou o padrao da plataforma. */
function fundoDoPainel(portal: PortalConfig | null): CSSProperties | undefined {
  if (!portal?.resolved) return undefined;
  const imagem = imagemSegura(portal.layout.login.background_image);
  if (imagem) {
    return {
      backgroundImage: `linear-gradient(rgba(10, 27, 61, 0.55), rgba(10, 27, 61, 0.55)), url(${JSON.stringify(imagem)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  const cores = portal.layout.common.gradient_background_colors.filter((cor) =>
    HEX.test(cor),
  );
  if (portal.layout.common.gradient_background && cores.length >= 2) {
    return { backgroundImage: `linear-gradient(135deg, ${cores.join(', ')})` };
  }
  return undefined;
}

/**
 * Moldura das telas publicas de acesso: painel de marca e coluna do formulario.
 *
 * Com a empresa reconhecida, o painel mostra o que ela configurou na aba Acesso
 * (titulo, os dois subtitulos, logotipo branco, degrade ou imagem de fundo), como a
 * tela de login do OnPoint. Sem empresa, mostra a marca da plataforma.
 */
export function PublicFrame({
  portal,
  children,
}: {
  portal: PortalConfig | null;
  children: ReactNode;
}) {
  const reconhecida = portal?.resolved ? portal : null;
  const logoBranco = reconhecida
    ? imagemSegura(reconhecida.layout.common.logo_white)
    : null;
  const logo = reconhecida ? imagemSegura(reconhecida.layout.common.logo) : null;

  const marca = reconhecida
    ? reconhecida.layout.login.subtitle1 || reconhecida.display_name
    : 'SeguraCore';
  const titulo = reconhecida
    ? reconhecida.layout.login.title || reconhecida.display_name
    : 'A operacao de garantia inteira, em um lugar so.';
  const apoio = reconhecida
    ? reconhecida.layout.login.subtitle2
    : 'Da originacao a emissao: credito, subscricao, juridico, formalizacao e carteira, com trilha de auditoria e controle de acesso em cada etapa.';

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_480px]">
      {/* Painel de marca. Escondido em telas pequenas, onde o formulario e o que
          importa. */}
      <section
        className="relative hidden overflow-hidden bg-linear-to-br from-brand-950 via-brand-900 to-brand-800 lg:block"
        style={fundoDoPainel(portal)}
        data-testid="painel-de-marca"
      >
        {reconhecida ? null : (
          <>
            <div
              aria-hidden="true"
              className="absolute -top-32 -left-24 size-[32rem] rounded-full bg-accent-500/15 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="absolute right-[-6rem] bottom-[-8rem] size-[26rem] rounded-full bg-brand-500/20 blur-3xl"
            />
          </>
        )}

        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            {logoBranco ? (
              <img src={logoBranco} alt="" className="h-14 w-auto" />
            ) : (
              <LogoSymbol size={56} />
            )}
            <span className="text-2xl font-semibold tracking-tight text-white">
              {marca}
            </span>
          </div>

          <div className="max-w-lg">
            <h2 className="text-3xl leading-tight font-semibold text-white">
              {titulo}
            </h2>
            {apoio ? (
              <p className="mt-4 text-sm leading-relaxed text-white/70">{apoio}</p>
            ) : null}
          </div>

          <p className="text-xs text-white/40">
            {reconhecida
              ? 'Plataforma SeguraCore'
              : 'Plataforma de gestao para seguradoras e corretores'}
          </p>
        </div>
      </section>

      <section className="flex items-center justify-center bg-canvas px-5 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center gap-2 text-center">
            {logo ? (
              <img
                src={logo}
                alt={reconhecida?.display_name ?? ''}
                className="h-16 w-auto"
              />
            ) : (
              <LogoHorizontal height={64} />
            )}
            {reconhecida ? (
              <p className="text-xs text-ink-500 lg:hidden">{titulo}</p>
            ) : null}
          </div>
          {children}
        </div>
      </section>
    </div>
  );
}
